"use client";

import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const productDatabase: Record<string, {
  id: string;
  name: string;
  nameEn: string;
  category: string;
  price: string;
  moq: string;
  unit: string;
  description: string;
  features: string[];
  specs: {label: string;value: string;}[];
  images: {src: string;alt: string;}[];
  inStock: boolean;
  tags: string[];
}> = {
  "laundry-detergent": {
    id: "laundry-detergent",
    name: "濃縮洗衣液",
    nameEn: "Concentrated Laundry Detergent",
    category: "清潔用品",
    price: "HK$ 38",
    moq: "最低訂購量: 24 瓶",
    unit: "1L / 瓶",
    description: "採用先進濃縮配方，一瓶可替代兩瓶普通洗衣液。溫和不傷手，對頑固污漬有卓越去除效果，適合各種纖維面料，冷熱水均可使用。",
    features: ["高效去污，一瓶頂兩瓶", "溫和護膚配方，不傷手", "適合機洗及手洗", "清新花香，持久留香", "可降解配方，環境友善"],
    specs: [
    { label: "容量", value: "1000ml" },
    { label: "香型", value: "清新花香" },
    { label: "適用", value: "棉、麻、化纖等各類面料" },
    { label: "用量", value: "每次 20-30ml" },
    { label: "原產地", value: "中國上海" },
    { label: "保質期", value: "3年" }],

    images: [
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8c751d2-1767878728973.png",
      alt: "濃縮洗衣液主圖，藍色瓶裝1升裝，清晰展示產品正面"
    },
    {
      src: "https://images.unsplash.com/photo-1646013976311-d7de74c2f679",
      alt: "洗衣液使用場景圖，洗衣機旁放置洗衣液"
    },
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_1da8bfb9f-1767330061401.png",
      alt: "洗衣液特寫，展示產品質感與包裝細節"
    }],

    inStock: true,
    tags: ["暢銷", "批發優惠"]
  },
  "dish-soap": {
    id: "dish-soap",
    name: "天然洗碗液",
    nameEn: "Natural Dish Soap",
    category: "清潔用品",
    price: "HK$ 28",
    moq: "最低訂購量: 36 瓶",
    unit: "500ml / 瓶",
    description: "採用天然植物成分，有效去除油脂和食物殘渣，泡沫豐富易沖洗。溫和不傷皮膚，適合敏感肌膚人士使用，同時保護您的餐具光澤。",
    features: ["天然植物成分", "豐富泡沫，易沖洗", "溫和護膚，適合敏感肌", "有效去油去污", "清新柑橘香型"],
    specs: [
    { label: "容量", value: "500ml" },
    { label: "香型", value: "清新柑橘" },
    { label: "成分", value: "天然植物提取物" },
    { label: "pH值", value: "中性 (6.5-7.5)" },
    { label: "原產地", value: "中國上海" },
    { label: "保質期", value: "2年" }],

    images: [
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_1da8bfb9f-1767330061401.png",
      alt: "天然洗碗液主圖，綠色環保包裝500毫升裝"
    },
    {
      src: "https://images.unsplash.com/photo-1690243689195-63490060eda8",
      alt: "洗碗液使用場景，廚房洗碗槽旁的產品擺放"
    },
    {
      src: "https://images.unsplash.com/photo-1625060775383-3411ab3739f4",
      alt: "洗碗液泡沫效果展示，豐富細膩的泡沫"
    }],

    inStock: true,
    tags: ["天然配方"]
  },
  "shampoo": {
    id: "shampoo",
    name: "滋養洗髮水",
    nameEn: "Nourishing Shampoo",
    category: "個人護理",
    price: "HK$ 52",
    moq: "最低訂購量: 24 瓶",
    unit: "400ml / 瓶",
    description: "富含天然植物精華，深層滋養頭皮與髮絲，改善乾燥受損發質。溫和清潔配方，每日使用不傷頭皮，令頭髮更加柔順亮澤，適合各類髮質。",
    features: ["深層滋養修護", "溫和日常清潔", "改善乾燥受損", "持久香氣留存", "適合各類髮質"],
    specs: [
    { label: "容量", value: "400ml" },
    { label: "香型", value: "花香調" },
    { label: "適用髮質", value: "所有髮質，尤其乾性" },
    { label: "主要成分", value: "胺基酸、植物精華" },
    { label: "原產地", value: "中國上海" },
    { label: "保質期", value: "3年" }],

    images: [
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_12246f2a7-1765181565379.png",
      alt: "滋養洗髮水主圖，白色優雅瓶裝400毫升"
    },
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_1708c6bc1-1765621816886.png",
      alt: "洗髮水使用場景，浴室架上整齊擺放的護髮系列"
    },
    {
      src: "https://images.unsplash.com/photo-1701992678962-41703126549c",
      alt: "洗髮水產品細節圖，展示瓶蓋設計與標籤"
    }],

    inStock: true,
    tags: ["新品"]
  },
  "kitchen-cleaner": {
    id: "kitchen-cleaner",
    name: "廚房多功能清潔劑",
    nameEn: "Kitchen Multi-Purpose Cleaner",
    category: "廚房用品",
    price: "HK$ 45",
    moq: "最低訂購量: 24 瓶",
    unit: "750ml / 瓶",
    description: "專為廚房設計的多功能清潔噴霧，有效去除油脂、食物污漬及細菌。適用於爐灶、抽油煙機、廚房台面等各種廚房表面，使用方便，清潔效果顯著。",
    features: ["強效去油去污", "99.9%殺菌消毒", "適用多種廚房表面", "噴霧設計，使用方便", "清新檸檬香型"],
    specs: [
    { label: "容量", value: "750ml" },
    { label: "香型", value: "清新檸檬" },
    { label: "適用表面", value: "爐灶、台面、抽油煙機等" },
    { label: "殺菌率", value: "99.9%" },
    { label: "原產地", value: "中國上海" },
    { label: "保質期", value: "2年" }],

    images: [
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_10de60a26-1765801878220.png",
      alt: "廚房多功能清潔劑主圖，噴霧瓶設計750毫升裝"
    },
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4c1038d-1767334771923.png",
      alt: "清潔劑使用場景，清潔廚房台面的示範圖"
    },
    {
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3d4e299-1766997885759.png",
      alt: "清潔劑產品細節，展示噴頭設計與標籤資訊"
    }],

    inStock: true,
    tags: ["熱銷"]
  }
};

const relatedProducts = [
{
  id: "dish-soap",
  name: "天然洗碗液",
  category: "清潔用品",
  price: "HK$ 28",
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_170431508-1769689096620.png",
  alt: "天然洗碗液縮圖，綠色環保包裝"
},
{
  id: "shampoo",
  name: "滋養洗髮水",
  category: "個人護理",
  price: "HK$ 52",
  img: "https://images.unsplash.com/photo-1701992679059-8631c83df108",
  alt: "滋養洗髮水縮圖，白色優雅瓶裝"
},
{
  id: "kitchen-cleaner",
  name: "廚房清潔劑",
  category: "廚房用品",
  price: "HK$ 45",
  img: "https://images.unsplash.com/photo-1584969692915-882ae5013447",
  alt: "廚房清潔劑縮圖，噴霧瓶設計"
}];


export default function ProductDetailInteractive() {
  const searchParams = useSearchParams();
  const productId = searchParams.get("id") || "laundry-detergent";
  const product = productDatabase[productId] || productDatabase["laundry-detergent"];

  const [activeImg, setActiveImg] = useState(0);
  const [activeTab, setActiveTab] = useState<"features" | "specs">("features");

  useEffect(() => {
    setActiveImg(0);
  }, [productId]);

  const whatsappMsg = encodeURIComponent(
    `你好，我想查詢「${product.name}」的批發報價，謝謝。`
  );

  return (
    <div className="container-main py-12 pb-24">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-white/30 mb-8">
        <Link href="/homepage" className="hover:text-white/60 transition-colors">首頁</Link>
        <span>/</span>
        <Link href="/products" className="hover:text-white/60 transition-colors">產品目錄</Link>
        <span>/</span>
        <span className="text-gold-DEFAULT">{product.name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        {/* Image Gallery */}
        <div className="space-y-4">
          {/* Main image */}
          <div className="relative rounded-2xl overflow-hidden bg-brand-card aspect-square">
            <AppImage
              src={product.images[activeImg].src}
              alt={product.images[activeImg].alt}
              fill
              className="object-cover"
              priority />
            
            {/* Tags overlay */}
            <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
              {product.tags.map((tag) =>
              <span key={tag} className="category-badge text-xs px-3 py-1 rounded-full font-semibold">
                  {tag}
                </span>
              )}
            </div>
            {!product.inStock &&
            <div className="absolute inset-0 bg-brand-bg/60 flex items-center justify-center">
                <span className="text-white/70 text-sm font-semibold border border-white/30 px-4 py-2 rounded-full">
                  暫時缺貨
                </span>
              </div>
            }
          </div>

          {/* Thumbnails */}
          <div className="flex gap-3">
            {product.images.map((img, i) =>
            <button
              key={i}
              onClick={() => setActiveImg(i)}
              className={`relative w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
              activeImg === i ? "border-gold-DEFAULT" : "border-transparent opacity-50 hover:opacity-80"}`
              }>
              
                <AppImage
                src={img.src}
                alt={`${product.name} 縮略圖 ${i + 1}`}
                fill
                className="object-cover" />
              
              </button>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="flex flex-col">
          <div className="mb-2">
            <span className="text-xs text-white/35 uppercase tracking-wider">{product.category}</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-white mb-1">
            {product.name}
          </h1>
          <p className="text-white/40 text-sm mb-6">{product.nameEn}</p>

          {/* Price */}
          <div className="glass-card-gold rounded-xl p-5 mb-6">
            <div className="flex items-end gap-3 mb-2">
              <span className="text-3xl font-bold text-gold-DEFAULT">{product.price}</span>
              <span className="text-white/40 text-sm mb-1">{product.unit}</span>
            </div>
            <p className="text-xs text-white/40">{product.moq}</p>
          </div>

          {/* Description */}
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            {product.description}
          </p>

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex gap-0 mb-5 border-b border-white/[0.08]">
              {(["features", "specs"] as const).map((tab) =>
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2.5 text-sm font-medium transition-all duration-200 border-b-2 -mb-px ${
                activeTab === tab ?
                "border-gold-DEFAULT text-gold-DEFAULT" : "border-transparent text-white/40 hover:text-white/70"}`
                }>
                
                  {tab === "features" ? "產品特點" : "規格參數"}
                </button>
              )}
            </div>

            {activeTab === "features" ?
            <ul className="space-y-2.5">
                {product.features.map((f) =>
              <li key={f} className="flex items-start gap-2.5 text-sm text-white/70">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                    {f}
                  </li>
              )}
              </ul> :

            <div className="grid grid-cols-2 gap-2">
                {product.specs.map((s) =>
              <div key={s.label} className="glass-card rounded-lg p-3">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">{s.label}</p>
                    <p className="text-sm text-white font-medium">{s.value}</p>
                  </div>
              )}
              </div>
            }
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-3 mt-auto">
            <a
              href={`https://wa.me/85294927816?text=${whatsappMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn flex items-center justify-center gap-3 py-4 rounded-xl text-white font-bold text-base">
              
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              WhatsApp 即時查詢報價
            </a>
            <Link
              href="/contact"
              className="btn-gold-outline flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold">
              
              <Icon name="EnvelopeIcon" size={16} />
              發送查詢表單
            </Link>
          </div>
        </div>
      </div>

      {/* Related Products */}
      <div>
        <div className="section-divider mb-10" />
        <h2 className="font-serif text-2xl font-bold text-white mb-6">相關產品</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {relatedProducts.
          filter((r) => r.id !== productId).
          slice(0, 3).
          map((r) =>
          <Link key={r.id} href={`/product-detail?id=${r.id}`} className="product-card">
                <div className="relative h-40 overflow-hidden">
                  <AppImage src={r.img} alt={r.alt} fill className="object-cover product-img" />
                  <div className="img-overlay absolute inset-0" />
                </div>
                <div className="p-4">
                  <p className="text-[10px] text-white/35 uppercase tracking-wider mb-1">{r.category}</p>
                  <h3 className="font-serif text-base text-white font-semibold mb-1">{r.name}</h3>
                  <p className="text-gold-DEFAULT text-sm font-bold">{r.price}</p>
                </div>
              </Link>
          )}
        </div>
      </div>
    </div>);

}